import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { Calendar } from '@/shared/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { CalendarIcon, ChevronDownIcon, Loader2, Trash2 } from 'lucide-react';
import { endOfMonth, format } from 'date-fns';
import {
  cn,
  parseDateFromString,
  toInclusivePeriodEnd,
  utcIsoToLocalCalendarDate,
} from '@/shared/utils/date-utils';
import { useCreateServiceLineOverage, useRemoveServiceLineOverage } from '@/domains/service-lines/services/account-service-lines';
import { OverageData, OverageStatus, ServiceLine } from '@/shared/types/serviceLineTypes';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showError, showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { Organization } from '@/shared/types/userTypes';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/shared/ui/dropdown-menu';
import { Separator } from '@/shared/ui/separator';
import { getOverageInfo } from '@/shared/utils/serviceLineCalculations';
import GenericAlertDialog from '@/shared/components/GenericAlertDialog';

const DEFAULT_DATA_AMOUNT = 100;
const DATA_BLOCK_OPTIONS = [50, 100, 200, 500] as const;

interface OverageSettingsDialogProps {
  open: boolean;
  serviceLine: ServiceLine;
  organization?: Organization;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

function getEndOfThisMonth(): Date {
  const date = endOfMonth(new Date());
  date.setHours(0, 0, 0, 0);
  return date;
}

function getTomorrow(): Date {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 1);
  return date;
}

function calendarDateFromFormValue(dateString: string | null | undefined): Date | undefined {
  if (!dateString) return undefined;
  if (dateString.includes('T') || dateString.includes('Z')) {
    return utcIsoToLocalCalendarDate(dateString);
  }
  return parseDateFromString(dateString);
}

function toCalendarDateString(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function createDefaultFormData(endOfMonthDate: Date): OverageData {
  return {
    mode: 'LIMITED',
    dataAmount: DEFAULT_DATA_AMOUNT,
    dataUnit: 'GB',
    overageEndDate: toCalendarDateString(endOfMonthDate),
  };
}

function buildFormDataFromServiceLine(serviceLine: ServiceLine, endOfMonthDate: Date): OverageData {
  if (!serviceLine.overage) {
    return createDefaultFormData(endOfMonthDate);
  }

  const inclusiveEnd = toInclusivePeriodEnd(serviceLine.overage.overageEndDate);
  const endCalendarDate = calendarDateFromFormValue(inclusiveEnd);
  const isFutureOrToday = endCalendarDate != null && endCalendarDate >= getTomorrow();

  return {
    mode: serviceLine.overage.mode ?? 'LIMITED',
    dataAmount: serviceLine.overage.dataAmount ?? DEFAULT_DATA_AMOUNT,
    dataUnit: 'GB',
    overageEndDate: isFutureOrToday && endCalendarDate
      ? toCalendarDateString(endCalendarDate)
      : toCalendarDateString(endOfMonthDate),
  };
}

export const OverageSettingsDialog = ({
  open,
  onOpenChange,
  onSuccess,
  organization,
  serviceLine,
}: OverageSettingsDialogProps) => {
  const overageType = organization?.overageType ?? 'FREE';
  const hasExistingOverage = Boolean(serviceLine.overage);
  const overageInfo = useMemo(() => getOverageInfo(serviceLine.overage), [serviceLine.overage]);

  const tomorrow = useMemo(() => getTomorrow(), [open]);
  const endOfThisMonthDate = useMemo(() => getEndOfThisMonth(), [open]);

  const [formData, setFormData] = useState<OverageData>(() => createDefaultFormData(getEndOfThisMonth()));
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const createOverage = useCreateServiceLineOverage();
  const removeOverage = useRemoveServiceLineOverage();

  const selectedEndDate = calendarDateFromFormValue(formData.overageEndDate);
  const isPending = createOverage.isPending || removeOverage.isPending;
  const isActiveOverage = overageInfo.status === OverageStatus.ACTIVE;

  useEffect(() => {
    if (!open) {
      setFormData(createDefaultFormData(getEndOfThisMonth()));
      setConfirmDeleteOpen(false);
      return;
    }
    setFormData(buildFormDataFromServiceLine(serviceLine, endOfThisMonthDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate only when dialog opens
  }, [open]);

  const patchForm = (patch: Partial<OverageData>) => {
    setFormData((prev) => {
      return {
        ...(prev ?? createDefaultFormData(endOfThisMonthDate)),
        ...patch,
      };
    });
  };

  const validateAndBuildPayload = (): OverageData | null => {
    const endCalendarDate = selectedEndDate ? new Date(selectedEndDate) : endOfThisMonthDate;

    if (endCalendarDate < tomorrow) {
      showError(ERROR.OVERAGE.END_DATE_IN_PAST);
      return null;
    }

    if (endCalendarDate > endOfThisMonthDate) {
      showError(ERROR.OVERAGE.END_DATE_IN_FUTURE);
      return null;
    }

    if (formData.mode === 'LIMITED' && (!formData.dataAmount || formData.dataAmount <= 0)) {
      showError(ERROR.OVERAGE.AMOUNT_MUST_BE_GREATER_THAN_0);
      return null;
    }

    return {
      mode: formData.mode,
      dataAmount: formData.mode === 'LIMITED' ? formData.dataAmount : undefined,
      dataUnit: formData.mode === 'LIMITED' ? 'GB' : undefined,
      overageEndDate: format(endCalendarDate, 'yyyy-MM-dd'),
    };
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const overageData = validateAndBuildPayload();
    if (!overageData) return;

    try {
      await createOverage.mutateAsync({
        serviceLineNumber: serviceLine.serviceLineNumber,
        overageData,
      });
      showSuccess(SUCCESS.SERVICE_LINE.ENABLED_OVERAGE);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.SERVICE_LINE.ENABLE_OVERAGE);
    }
  };

  const handleDelete = async () => {
    try {
      await removeOverage.mutateAsync({
        serviceLineNumber: serviceLine.serviceLineNumber,
      });
      showSuccess(SUCCESS.SERVICE_LINE.DISABLED_OVERAGE);
      setConfirmDeleteOpen(false);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.SERVICE_LINE.DISABLE_OVERAGE);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[520px] gap-0 p-0 overflow-hidden">
          <DialogHeader className="p-4">
            <DialogTitle className="text-xl">
              {hasExistingOverage ? 'Manage Overage' : 'Create Overage'}
            </DialogTitle>
            <DialogDescription>
              {hasExistingOverage
                ? 'Update or remove the overage plan for this service line.'
                : 'Configure a data overage plan for this service line.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave} className="flex flex-col">
            <div className="p-4 space-y-4">
              {hasExistingOverage && (
                <div className="flex items-center justify-between rounded-lg border bg-muted/40 p-2">
                  <div>
                    <p className="text-sm font-medium">Current overage plan</p>
                    <p className="text-sm text-muted-foreground">{overageInfo.info}</p>
                  </div>
                  <Badge
                    variant={overageInfo.status === OverageStatus.ACTIVE ? 'default' : overageInfo.status === OverageStatus.EXPIRED ? 'destructive' : 'outline'}
                    className="font-normal"
                  >
                    {overageInfo.status === OverageStatus.ACTIVE
                      ? 'Active'
                      : overageInfo.status === OverageStatus.EXPIRED
                        ? 'Expired'
                        : 'None'}
                  </Badge>
                </div>
              )}

              <div className="space-y-2">
                <div>
                  <Label>Overage plan</Label>
                  <p className="text-xs text-muted-foreground">
                    Choose a limited data block or unlimited overage.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={formData.mode === 'LIMITED' ? 'default' : 'outline'}
                    className="h-auto py-3 flex-col items-start gap-0.5"
                    onClick={() => patchForm({
                      mode: 'LIMITED',
                      dataAmount: formData.dataAmount ?? DEFAULT_DATA_AMOUNT,
                    })}
                  >
                    <span className="text-sm font-medium">Limited</span>
                    <span className={cn(
                      'text-xs font-normal',
                      formData.mode === 'LIMITED' ? 'text-primary-foreground/80' : 'text-muted-foreground',
                    )}>
                      Cap additional data
                    </span>
                  </Button>
                  <Button
                    type="button"
                    variant={formData.mode === 'UNLIMITED' ? 'default' : 'outline'}
                    className="h-auto py-3 flex-col items-start gap-0.5"
                    onClick={() => patchForm({
                      mode: 'UNLIMITED',
                      dataAmount: null,
                    })}
                  >
                    <span className="text-sm font-medium">Unlimited</span>
                    <span className={cn(
                      'text-xs font-normal',
                      formData.mode === 'UNLIMITED' ? 'text-primary-foreground/80' : 'text-muted-foreground',
                    )}>
                      No data cap
                    </span>
                  </Button>
                </div>
              </div>

              {formData.mode === 'LIMITED' && (
                <div className="space-y-2">
                  <div>
                    <Label htmlFor="overage-amount">Data amount (GB)</Label>
                    <p className="text-xs text-muted-foreground">
                      Extra data available beyond the base plan.
                    </p>
                  </div>
                  {overageType === 'DATA_BLOCK' ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-between font-normal"
                        >
                          <span>{formData.dataAmount ?? DEFAULT_DATA_AMOUNT} GB</span>
                          <ChevronDownIcon className="h-4 w-4 opacity-60" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
                        {DATA_BLOCK_OPTIONS.map((amount) => (
                          <DropdownMenuItem
                            key={amount}
                            onClick={() => patchForm({ dataAmount: amount })}
                            className={cn(
                              'text-sm font-normal',
                              (formData.dataAmount ?? DEFAULT_DATA_AMOUNT) === amount && 'bg-primary/10',
                            )}
                          >
                            {amount} GB
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <Input
                      id="overage-amount"
                      type="number"
                      min={1}
                      value={formData.dataAmount ?? DEFAULT_DATA_AMOUNT}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (value > 0) {
                          patchForm({ dataAmount: value });
                        }
                      }}
                    />
                  )}
                </div>
              )}

              <div className="space-y-2">
                <div>
                  <Label>Overage end date</Label>
                  <p className="text-xs text-muted-foreground">
                    Must be between tomorrow and the end of this month.
                    Defaults to {format(endOfThisMonthDate, 'MMM d, yyyy')} if unchanged.
                  </p>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !selectedEndDate && 'text-muted-foreground',
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedEndDate ? format(selectedEndDate, 'MMMM d, yyyy') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedEndDate}
                      onSelect={(date) => {
                        patchForm({
                          overageEndDate: toCalendarDateString(date ?? endOfThisMonthDate),
                        });
                      }}
                      disabled={(date) => date < tomorrow || date > endOfThisMonthDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <Separator />

            <DialogFooter className="p-4 gap-2">
              {hasExistingOverage && (
                <Button
                  type="button"
                  variant="destructive"
                  className="sm:mr-auto"
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={isPending}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {createOverage.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : hasExistingOverage ? (
                  'Update overage'
                ) : (
                  'Create overage'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <GenericAlertDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Delete overage?"
        description="This removes the current overage plan from the service line. You can create a new one later."
        confirmLabel="Delete overage"
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        loading={removeOverage.isPending}
      />
    </>
  );
};
